import React from 'react';
import { Form, Input, Button, Card, Typography, message, Row, Col, Checkbox, Divider, Alert, Select } from 'antd';
import { LockOutlined, UserOutlined, MailOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';

const { Title, Text } = Typography;

const RegisterSupport: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [selectedSource, setSelectedSource] = React.useState<string>('');

  const onFinishFailed = (info: { errorFields?: { name: (string | number)[]; errors: string[] }[] }) => {
    const first = info?.errorFields?.[0];
    if (first?.errors?.length) {
      message.warning(first.errors[0]);
    } else {
      message.warning('Проверьте форму');
    }
  };

  const onFinish = async (values: any) => {
    if (!values.agree_terms || !values.agree_privacy) {
      message.error('Необходимо согласиться с соглашениями');
      return;
    }

    setLoading(true);
    try {
      const data: any = {
        email: values.email,
        username: values.username,
        password: values.password,
        role: 'support',
        inn: values.inn,
        contactName: values.contact_name,
        contactPhone: values.contact_phone,
        source: values.source === 'other' ? values.customSource : values.source
      };

      const result = await authAPI.register(data);

      if (result.pending) {
        message.success('Заявка отправлена на одобрение администратору.');
      } else {
        message.success('Регистрация успешна.');
      }
      navigate('/login', { replace: true });
    } catch (error: any) {
      const errMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Ошибка регистрации';
      message.error(typeof errMsg === 'string' ? errMsg : 'Ошибка регистрации');
    } finally {
      setLoading(false);
    }
  };

  const validatePasswordsMatch = (_: any, value: string) => {
    const password = form.getFieldValue('password');
    if (value && value !== password) {
      return Promise.reject('Пароли не совпадают');
    }
    return Promise.resolve();
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f0f2f5',
      padding: '10px'
    }}>
      <Row justify="center" style={{ width: '100%' }}>
        <Col xs={22} sm={18} md={14} lg={10} xl={8}>
          <Card
            style={{
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              borderRadius: '8px'
            }}
            variant="outlined"
          >
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                🛠️ Регистрация службы поддержки
              </Title>
              <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Создание учетной записи для сотрудника службы поддержки
              </Text>
            </div>

            <Alert
              message="Доступ по приглашению"
              description="Регистрация службы поддержки доступна только по специальной ссылке от администратора."
              type="info"
              showIcon
              style={{ marginBottom: '12px' }}
            />

            <Form
              form={form}
              name="registerSupport"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
              autoComplete="off"
              size="middle"
              layout="vertical"
            >
              <Form.Item
                label="ИНН организации"
                name="inn"
                rules={[
                  { required: true, message: 'Введите ИНН' },
                  { pattern: /^(\d{10}|\d{12})$/, message: 'ИНН: 10 или 12 цифр' }
                ]}
              >
                <Input placeholder="10 или 12 цифр" />
              </Form.Item>

              <Form.Item
                label="Контактное лицо"
                name="contact_name"
                rules={[{ required: true, message: 'Укажите контактное лицо' }]}
              >
                <Input placeholder="ФИО" />
              </Form.Item>

              <Form.Item
                label="Телефон"
                name="contact_phone"
                rules={[
                  { required: true, message: 'Укажите телефон' },
                  { pattern: /^\+?7[\s-]?9[\d\s-]{8,}$/, message: 'Некорректный номер телефона' }
                ]}
              >
                <Input placeholder="+7 (999) 123-45-67" />
              </Form.Item>

              <Form.Item
                label="Откуда вы о нас узнали?"
                name="source"
                rules={[{ required: true, message: 'Укажите источник информации' }]}
              >
                <Select 
                  placeholder="Выберите источник" 
                  allowClear
                  onChange={(value) => setSelectedSource(value)}
                >
                  <Select.Option value="google">Google поиск</Select.Option>
                  <Select.Option value="yandex">Яндекс поиск</Select.Option>
                  <Select.Option value="social_media">Социальные сети</Select.Option>
                  <Select.Option value="recommendation">Рекомендация знакомых</Select.Option>
                  <Select.Option value="employer">От работодателя</Select.Option>
                  <Select.Option value="advertisement">Реклама</Select.Option>
                  <Select.Option value="website">Сайт компании</Select.Option>
                  <Select.Option value="exhibition">Выставка/мероприятие</Select.Option>
                  <Select.Option value="other">Другое</Select.Option>
                </Select>
              </Form.Item>

              {selectedSource === 'other' && (
                <Form.Item
                  label="Укажите свой источник"
                  name="customSource"
                  rules={[{ required: true, message: 'Введите источник информации' }]}
                >
                  <Input placeholder="Например: коллега, реклама в метро, рекомендация друга..." />
                </Form.Item>
              )}

              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Введите email!' },
                  { type: 'email', message: 'Некорректный email!' }
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="your@email.com"
                  autoComplete="off"
                />
              </Form.Item>

              <Form.Item
                label="Имя пользователя"
                name="username"
                rules={[
                  { required: true, message: 'Введите имя пользователя!' },
                  { min: 3, message: 'Минимум 3 символа!' },
                  { pattern: /^[a-zA-Z0-9_]+$/, message: 'Только буквы, цифры и подчеркивание!' }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="username"
                  autoComplete="off"
                />
              </Form.Item>

              <Form.Item
                label="Пароль"
                name="password"
                rules={[
                  { required: true, message: 'Введите пароль!' },
                  { min: 6, message: 'Минимум 6 символов!' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Пароль"
                />
              </Form.Item>

              <Form.Item
                label="Повторите пароль"
                name="confirm_password"
                rules={[
                  { required: true, message: 'Повторите пароль!' },
                  { validator: validatePasswordsMatch }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Повтор пароля"
                />
              </Form.Item>

              <Form.Item
                name="agree_terms"
                valuePropName="checked"
                initialValue={false}
                rules={[
                  {
                    validator: (_, v) =>
                      v === true ? Promise.resolve() : Promise.reject(new Error('Необходимо согласие с пользовательским соглашением')),
                  },
                ]}
                style={{ marginBottom: '8px' }}
              >
                <Checkbox>
                  Я принимаю{' '}
                  <a href="/terms" target="_blank" rel="noreferrer">
                    пользовательское соглашение
                  </a>
                </Checkbox>
              </Form.Item>

              <Form.Item
                name="agree_privacy"
                valuePropName="checked"
                initialValue={false}
                rules={[
                  {
                    validator: (_, v) =>
                      v === true ? Promise.resolve() : Promise.reject(new Error('Необходимо согласие с политикой конфиденциальности')),
                  },
                ]}
                style={{ marginBottom: '8px' }}
              >
                <Checkbox>
                  Я согласен с{' '}
                  <a href="/privacy" target="_blank" rel="noreferrer">
                    политикой конфиденциальности
                  </a>
                </Checkbox>
              </Form.Item>

              <Form.Item style={{ marginBottom: '8px' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  style={{ width: '100%', height: '32px' }}
                >
                  Зарегистрироваться
                </Button>
              </Form.Item>
            </Form>

            <Divider style={{ margin: '12px 0' }} />

            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">
                Уже есть аккаунт?{' '}
                <Link to="/login" style={{ color: '#1890ff', fontWeight: 500 }}>
                  Войти
                </Link>
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default RegisterSupport;
